/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useState } from 'react'
import { PublishOutlined } from '@material-ui/icons'
import omit from 'lodash/omit'
import { useSnackbar } from 'notistack'
import { useHistory, useLocation } from 'react-router-dom'
import * as Analytics from 'core/common/analytics'
import useDisclosure from 'core/common/hooks/useDisclosure'
import i18n from 'core/common/i18n'
import FormikModel from 'core/common/models/FormikModel'
import { parseDatasheet } from 'core/common/utils/datasheet'
import * as Error from 'core/common/utils/error'
import { isArray } from 'core/common/utils/is'
import { getMessageAndFildsError } from 'core/common/utils/message'
import CardStep from 'core/components/CardStep'
import { Button, Typography } from 'core/components/Core'
import Publish from 'core/components/Icons/Publish'
import MessageErrorEdit from 'core/components/MessageErrorEdit'
import { useAppContext } from 'core/pages/ProductNew-v2/context/AppContext'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { updateProducts, createProducts, linkProducts } from 'core/services/http'

const ProductPublish = ({ custom = {}, formMode }) => {
  const { hasCategoryInfo, saveAllCustomProducts } = custom
  const { formData } = useFormContext()
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { categoryInfo } = useAppContext()
  const [skuErrors, setSkuErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const onRedirectGoToFinishPublish = () => {
    enqueueSnackbar(i18n.t('productNew.publication.skuPublished'), {
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    })
    const redirectGoToCatalogList = custom?.redirectRoutes?.goToCatalogList?.url || '/'

    return custom?.redirectRoutes?.goToCatalogList?.external
      ? window.location.replace(redirectGoToCatalogList)
      : history.push(redirectGoToCatalogList)
  }

  const onSubmit = async () => {
    const variationsQuantity = formData.variations ? formData.variations.length : 0
    Analytics.clickEventCreateProduct(formData.sku, variationsQuantity)

    setIsLoading(true)

    try {
      const copy = { ...formData }

      const { anatel, anvisa, mapa, modelo, garantia, variations } = copy

      copy.datasheet = parseDatasheet(copy.datasheet, { anatel, anvisa, mapa, modelo, garantia })

      copy.datasheet = copy.datasheet.map(datasheetItem => {
        const newDatasheetItem = { ...datasheetItem }

        delete newDatasheetItem.typeSelection
        delete newDatasheetItem.values

        return newDatasheetItem
      })

      const formikModel = new FormikModel(copy)

      const products = formikModel.toNewProductsArray()

      const body = products.map(item => {
        let productItem = { ...item }

        if (hasCategoryInfo) {
          const extraDataInfo = {
            extra_data: [
              {
                name: 'department',
                value: categoryInfo?.department?.name,
              },
              {
                name: 'productType',
                value: categoryInfo?.category?.name,
              },
            ],
          }

          productItem = { ...productItem, ...extraDataInfo }
        }

        return productItem
      })

      const createResponse = saveAllCustomProducts
        ? await saveAllCustomProducts(body, custom.createCustomProducts)
        : await createProducts({ form: body })

      if (formData.linkedProducts.length > 0) {
        await linkProducts({
          productsIds: formData.linkedProducts.map(linkedProduct => linkedProduct.id),
          groupId: createResponse.group_id,
        })

        const productsToUpdate = formikModel.toLinkedProductsArray(createResponse.group_id)

        await updateProducts({
          products: productsToUpdate.map(product => ({
            id: product.id,
            patch: omit(product, ['id', 'sku', 'active']),
          })),
        })
      }

      Analytics.ProductPublishedSuccessfully(formMode)
      onRedirectGoToFinishPublish()
    } catch (err) {
      const message = Error.message(err)
      Analytics.catchEventCreateProduct({ message })

      const errors = getMessageAndFildsError(err?.response?.data?.details?.json || {}, 0)
      const details = getMessageAndFildsError(err?.response?.data?.details?.json || {}, 1)

      enqueueSnackbar(
        <MessageErrorEdit
          message={message}
          errors={errors}
          variations={formData?.variations}
          details={details}
        />,
        { variant: 'error' }
      )

      if (isArray(err.response?.data?.details)) {
        setSkuErrors(extractSKUErrors(err.response?.data))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const extractSKUErrors = err => {
    return err.details.filter(detail => !Object.keys(detail).sku).map(detail => detail.sku)
  }

  return (
    <>
      <CardStep>
        <div className="d-flex justify-center flex-wrap">
          <div className="my-5 d-flex justify-center w-100">
            <Publish width={50} height={50} />
          </div>

          <Typography variant="h5" component="h2" className="mb-4">
            {i18n.t('productNew.publication.publishTitle')}
          </Typography>

          <Typography variant="body1" color="secondary" className="w-100 mb-4" align="center">
            {i18n.t('productNew.publication.publishDescription')}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<PublishOutlined />}
            onClick={onSubmit}
            loading={isLoading}
          >
            {i18n.t('productNew.actions.publish')}
          </Button>
        </div>
      </CardStep>
    </>
  )
}

export default memo(ProductPublish)
